import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Briefcase, Search } from 'lucide-react';

const ODOO_BASE_URL = 'https://erp.kcs-tech.com:8074';

const colors = {
  pageBg: '#FFFFFF',
  navy: '#001534',
  red: '#E4002B',
  white: '#FFFFFF',
  body: '#4B5563',
  muted: '#6B7280',
  line: '#E5E7EB',
  softBlue: '#EAF2FF',
  softRed: '#FFF1F3',
  success: '#0F766E',
  error: '#C0392B',
  overlay: 'rgba(0, 21, 52, 0.72)',
};

export default function OpenPositions() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${ODOO_BASE_URL}/api/careers/jobs`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load jobs');
        return res.json();
      })
      .then((data) => {
        const jobList = Array.isArray(data) ? data : data.jobs || data.data || [];
        setJobs(Array.isArray(jobList) ? jobList : []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const uniqueCountries = useMemo(() => {
    return Array.from(new Set(jobs.map(job => getJobCountry(job)).filter(Boolean))).sort();
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      const titleMatch = getJobTitle(job).toLowerCase().includes(searchTerm.toLowerCase());
      const countryMatch = locationFilter ? getJobCountry(job) === locationFilter : true;
      return titleMatch && countryMatch;
    });
  }, [jobs, searchTerm, locationFilter]);

  return (
    <main className="min-h-screen px-4 py-12 sm:px-6 lg:px-8" style={{ backgroundColor: colors.pageBg, color: colors.navy }}>
      <section className="mx-auto max-w-7xl">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <span
              className="mb-4 inline-flex rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em]"
              style={{color: colors.red }}
            >
              Careers
            </span>
            <h1 className="text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl" style={{ color: colors.navy }}>
              Open Positions
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7" style={{ color: colors.body }}>
              Explore current openings and apply directly with your profile details.
            </p>
          </div>

          {!loading && !error && (
            <div
              className="w-fit rounded-lg border px-5 py-3 text-sm font-semibold shadow-sm"
              style={{ backgroundColor: colors.white, borderColor: colors.line, color: colors.navy }}
            >
              {filteredJobs.length === 1 ? '1 current opening' : `${filteredJobs.length} current openings`}
            </div>
          )}
        </div>

        {!loading && !error && jobs.length > 0 && (
          <div className="mb-8 flex flex-col gap-4 sm:flex-row">
            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search size={18} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search by job title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full rounded-md border py-3 pl-10 pr-3 text-sm outline-none"
                style={{ borderColor: colors.line, color: colors.navy, backgroundColor: colors.white }}
              />
            </div>
            
            <div className="relative sm:w-64 shrink-0">
              <select
                aria-label="Filter by country"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="block w-full appearance-none rounded-md border py-3 pl-4 pr-10 text-sm outline-none cursor-pointer"
                style={{ borderColor: colors.line, color: colors.navy, backgroundColor: colors.white }}
              >
                <option value="">All Countries</option>
                {uniqueCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              </div>
            </div>
          </div>
        )}

        {loading && <StatePanel title="Loading open positions..." />}

        {error && (
          <StatePanel
            title="Could not load jobs"
            message={error}
            tone="error"
          />
        )}

        {!loading && !error && jobs.length === 0 && (
          <StatePanel
            title="No open positions right now"
            message="Please check back soon for new opportunities."
          />
        )}

        {!loading && !error && jobs.length > 0 && filteredJobs.length === 0 && (
          <StatePanel
            title="No jobs found"
            message="Try adjusting your search or filter criteria."
          />
        )}

        {!loading && !error && filteredJobs.length > 0 && (
          <div className="flex flex-col gap-4">
            {filteredJobs.map((job) => (
              <JobCard key={job.id} job={job} onApply={() => navigate(`/careers/open-positions/${job.id}`, { state: { job } })} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

function JobCard({ job, onApply }) {
  const title = getJobTitle(job);
  const metaItems = [
    job.department || 'General',
    job.location,
    job.employment_type,
  ].filter(Boolean);

  return (
    <article
      className="group flex flex-col sm:flex-row sm:items-center justify-between rounded-lg border p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl"
      style={{ backgroundColor: colors.white, borderColor: colors.line }}
    >
      <div className="flex-1 pr-6">
        <h2 className="text-xl font-bold leading-snug" style={{ color: colors.navy }}>
          {title}
        </h2>
        
        <div className="mt-3 flex flex-wrap items-center gap-6 text-sm font-medium" style={{ color: colors.body }}>
          {job.employment_type && (
            <span className="flex items-center gap-1.5">
              <Briefcase size={16} className="text-gray-400" />
              {job.employment_type}
            </span>
          )}
          {job.location && (
            <span className="flex items-center gap-1.5">
              <MapPin size={16} className="text-gray-400" />
              {job.location}
            </span>
          )}
        </div>
        
        <p className="mt-4 line-clamp-2 text-sm leading-6" style={{ color: colors.body }}>
          {job.summary || 'We are looking for talented professionals who are ready to grow with Kapil Technologies.'}
        </p>
      </div>

      <div className="mt-6 sm:mt-0 sm:w-auto">
        <button
          type="button"
          className="w-full sm:w-auto rounded-md px-6 py-3 text-sm font-semibold transition duration-300 hover:shadow-lg whitespace-nowrap"
          style={{ backgroundColor: '#00B9E8', color: colors.white }}
          onClick={onApply}
        >
          Apply Now
        </button>
      </div>
    </article>
  );
}

function StatePanel({ title, message, tone = 'default' }) {
  const isError = tone === 'error';

  return (
    <div
      className="rounded-lg border px-6 py-12 text-center shadow-sm"
      style={{ backgroundColor: colors.white, borderColor: colors.line }}
    >
      <h2 className="text-xl font-bold" style={{ color: isError ? colors.error : colors.navy }}>
        {title}
      </h2>
      {message && (
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6" style={{ color: colors.body }}>
          {message}
        </p>
      )}
    </div>
  );
}

function getJobTitle(job) {
  return job.title || job.name || 'Open Role';
}

// Some careers records include the country only in the job title.
const countryPatterns = [
  ['India', /\b(india|hyderabad|bangalore|bengaluru|chennai|pune)\b/i],
  ['Singapore', /\bsingapore\b/i],
  ['Indonesia', /\b(indonesia|tangerang|bsd)\b/i],
  ['United States', /\b(us|usa|united states|cheyenne|wyoming|america)\b/i],
];

function getJobCountry(job) {
  const location = typeof job.location === 'string' ? job.location.trim() : '';
  const locationCountry = countryPatterns.find(([, pattern]) => pattern.test(location));
  if (locationCountry) return locationCountry[0];
  const titleCountry = countryPatterns.find(([, pattern]) => pattern.test(getJobTitle(job)));
  return titleCountry ? titleCountry[0] : location;
}
